import type { GroupChild, WorkspaceGroup } from './types';

/** Previews on a group card. A larger group shows a counter of the hidden DIRECT children. */
export const PREVIEW_LIMIT = 4;

export interface GroupPreview {
  shown: GroupChild[];
  hidden: number;
}

export function groupPreview(children: readonly GroupChild[]): GroupPreview {
  return {
    shown: children.slice(0, PREVIEW_LIMIT),
    hidden: Math.max(children.length - PREVIEW_LIMIT, 0),
  };
}

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });

/** `+3`, `+100`, `+1.2K`, `+10K`: narrow in every language, whatever the number. */
export function hiddenLabel(hidden: number): string {
  return `+${hidden < 1000 ? String(hidden) : compact.format(hidden)}`;
}

/** The ancestors of a group from the top down, ending with the group itself. */
export function groupPath(groups: readonly WorkspaceGroup[], id: string | null | undefined) {
  const by = new Map(groups.map((g) => [g.id, g]));
  const path: WorkspaceGroup[] = [];
  const seen = new Set<string>();
  let current = id ? by.get(id) : undefined;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parent_group_id ? by.get(current.parent_group_id) : undefined;
  }
  return path;
}

/** Every group below `id` (not itself), without one request per level: the list is complete. */
export function groupDescendants(groups: readonly WorkspaceGroup[], id: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const group of groups) {
    if (group.parent_group_id !== null) {
      children.set(group.parent_group_id, [
        ...(children.get(group.parent_group_id) ?? []),
        group.id,
      ]);
    }
  }
  const found = new Set<string>();
  const stack = [id];
  while (stack.length > 0) {
    for (const child of children.get(stack.pop() ?? '') ?? []) {
      if (!found.has(child)) {
        found.add(child);
        stack.push(child);
      }
    }
  }
  return found;
}

/** Where a GROUP may go (the server checks again): not into itself, a descendant or too deep. */
export function groupDestinations(
  groups: readonly WorkspaceGroup[],
  moving: string,
  maxDepth: number,
): Set<string> {
  const blocked = groupDescendants(groups, moving);
  blocked.add(moving);
  const depthOf = (id: string) => groupPath(groups, id).length;
  const below = [...groupDescendants(groups, moving)].map((id) => depthOf(id) - depthOf(moving));
  const height = 1 + Math.max(0, ...below);
  return new Set(
    groups.filter((g) => !blocked.has(g.id) && depthOf(g.id) + height <= maxDepth).map((g) => g.id),
  );
}

/** The canonical address of a group: its workspace page with the group (and its section) open. */
export function groupAddress(group: {
  id: string;
  workspace_id: string;
  section_id: string | null;
}): string {
  return `/app/workspaces/${group.workspace_id}?group=${group.id}${
    group.section_id ? `&folder=${group.section_id}` : ''
  }`;
}
